import dynamicImport from 'components/dynamicImport';
import { localizable } from 'components/Localization';
import { computed, extendObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import {
  DefaultButton,
  PrimaryButton,
} from 'office-ui-fabric-react/lib/Button';
import { Checkbox } from 'office-ui-fabric-react/lib/Checkbox';
import { Dropdown } from 'office-ui-fabric-react/lib/Dropdown';
import { Image, ImageFit } from 'office-ui-fabric-react/lib/Image';
import { Label } from 'office-ui-fabric-react/lib/Label';
import {
  MessageBar,
  MessageBarType,
} from 'office-ui-fabric-react/lib/MessageBar';
import { Panel, PanelType } from 'office-ui-fabric-react/lib/Panel';
import React, { Component, Fragment } from 'react';
import Dropzone from 'react-dropzone';
import BehalfOfAgentsDropdown from '../BehalfOfAgentsDropdown';
import { TermsLabel } from '../TermsLabel';
import { SaleCreateStore } from './stores/SaleCreateStore';

const SaleCreatePanelContent = dynamicImport({
  component: '/containers/Overview/SaleCreatePanelContent',
});

const previewProperties = {
  imageFit: ImageFit.contain,
  width: 350,
  height: 350,
};

@inject('sale', 'user', 'app')
@localizable
@observer
class SaleCreatePanel extends Component {
  @observable image;
  @observable dropzoneState = { active: false, uploadInProgress: false };
  dropzoneRef;
  @observable uploadError;

  @observable data = {};

  @observable isContentValid = false;
  @observable toc;

  constructor(props) {
    super(props);
    this.sale = new SaleCreateStore(props.user, props.sale, props.app);
    // this.sale.selectedAgent = props.user.profile;
    this.sale.isTest = props.isTest;

    const config = this.sale.config;

    extendObservable(this.data, {
      mls:
        config.mlsTypes && config.mlsTypes.length > 0 ? config.mlsTypes[0] : '',
    });
  }

  async componentDidMount() {
    await this.sale.loadAgents();

    if (!!this.sale.agents && this.sale.agents.length === 1) {
      this.sale.selectedAgent = this.sale.agents[0];
    }
  }

  @computed get tocDisabled() {
    const config = this.sale.config;

    if (!config.saleCreateRequireTC) {
      return true;
    }

    return (
      typeof config.disableSaleToC !== 'undefined' &&
      config.disableSaleToC.includes(this.props.user.profile.tenant)
    );
  }

  @computed get canInviteOnly() {
    return this.data.mls === 'tenant' || this.data.mls === 'everyone';
  }

  async handleUpload(files) {
    this.uploadError = null;
    this.dropzoneState.uploadInProgress = true;

    var file = files[0]; // upload in case of multiselect, upload only the first file
    try {
      this.image = await this.sale.upload(file);
    } catch (error) {
      this.uploadError = this.parseUploadError(error);
    }

    this.dropzoneState.uploadInProgress = false;
  }

  parseUploadError(error) {
    var { t } = this.props;
    if (
      typeof error.response !== 'undefined' &&
      typeof error.response.error !== 'undefined' &&
      typeof error.response.error.message !== 'undefined'
    ) {
      // try to detect known errors
      var res = /Raw file format ([^\s]+) not allowed/i.exec(
        error.response.error.message
      );
      if (res !== null)
        return t('File extension is not supported', { extension: res[1] });
      res = /File size too large. Got ([\d]+). Maximum is ([\d]+)/i.exec(
        error.response.error.message
      );
      if (res !== null)
        return t('File size exceeds allowed limit', {
          size: res[1],
          allowed: res[2],
        });
      // no luck, use the raw error message from Cloudinary
      return error.response.error.message;
    }
    // fallback to the error object
    return error.message;
  }

  generatePreviewUrl({ id }) {
    return `https://res.cloudinary.com/auctiontech/image/upload/c_pad,b_white,h_${previewProperties.height},w_${previewProperties.width}/${id}.jpg`;
  }

  async handleRemoveImage() {
    try {
      await this.sale.remove(this.image.metadata.deleteToken);
    } catch (error) {}
    this.image = null;
  }

  localHandleChange = (field) => (ev, option, index, value) => {
    this.handleChanged(field, option, index, value);
  };

  handleChanged = (field, option, index, value) => {
    if (typeof index === 'undefined' && typeof value === 'undefined') {
      this.data[field] = option; // textbox
    } else {
      if (option) {
        this.data[field] = option.key; // dropdown
      } else if (value) {
        this.data[field] = value; // combobox
      }
    }
  };

  handleContentIsValidChanged = (isValid) => {
    this.isContentValid = isValid;
  };

  @computed get isValid() {
    return (
      (this.image || this.sale.isTest) &&
      this.isContentValid &&
      (this.tocDisabled || this.toc) &&
      this.sale.canCreateSale &&
      this.sale.selectedAgent
    );
  }

  handleTocChange = (_, isChecked) => {
    this.toc = isChecked;
  };

  handleSaleTestChange = (_, isChecked) => {
    this.sale.isTest = isChecked;
  };

  handleClose = () => {
    const { onClose } = this.props;
    if (onClose) onClose();
  };

  handleCreate = () => {
    const { app } = this.props;
    const imageUrl = this.image ? this.image.url : null;

    const data = Object.assign(this.data, {
      image: imageUrl,
      images: imageUrl ? [{ url: imageUrl }] : [],
      currency_code: app.currency.code,
      inviteOnly: this.data.inviteOnly && this.canInviteOnly,
    });
    if (app.data && app.data.sale_country)
      data.sale_country = app.data.sale_country;

    this.sale.start(data);
  };

  handleAgentChanged = (e) => {
    this.sale.selectedAgent = e;
  };

  handleInviteOnly = (_, inviteOnly) => {
    this.data.inviteOnly = inviteOnly;
  };

  handleIsMasterSale = (_, value) => {
    this.sale.isMasterSale = value;
  };

  handleMasterSaleId = (_, option) => {
    console.log('🚀 ~ SaleCreatePanel ~ option:', option);
    this.sale.masterSaleId = option.key;
  };

  handleSelfRegistration = (_, value) => {
    this.sale.selfRegistration = value;
  };

  render() {
    const { isTest, t, intl, app, user } = this.props;
    const valid = this.isValid;
    const config = this.sale.config;

    return (
      <Panel
        headerText={isTest ? t('Start new test sale') : t('Start new sale')}
        type={PanelType.medium}
        isOpen={true}
        onRenderFooterContent={() => (
          <div className="ms-Grid-row">
            <div className="ms-Grid">
              {!this.tocDisabled && (
                <div className="ms-Grid-col ms-sm12">
                  <Checkbox
                    onChange={this.handleTocChange}
                    label={t('I agree with terms and conditions')}
                    onRenderLabel={() => (
                      <TermsLabel app={app} t={t} user={user} />
                    )}
                  />
                </div>
              )}
              <div
                className="ms-Grid-col ms-sm12 "
                style={{ paddingTop: '15px' }}
              >
                <DefaultButton
                  onClick={this.handleClose}
                  style={{ marginRight: '8px' }}
                >
                  {t('Cancel')}
                </DefaultButton>
                <PrimaryButton onClick={this.handleCreate} disabled={!valid}>
                  {t('Add')}
                </PrimaryButton>
              </div>
            </div>
          </div>
        )}
        isFooterAtBottom={true}
        onDismiss={this.handleClose}
      >
        <div className="ms-Grid">
          {isTest && (
            <div className="ms-Grid-row">
              <div className="ms-Grid-col ms-sm12 ms-lg12">
                <MessageBar>{t('test_sale_explanation')}</MessageBar>
              </div>
            </div>
          )}

          <div className="ms-Grid-row">
            <div className="ms-Grid-col ms-sm12">
              {!this.image && (
                <Dropzone
                  disableClick
                  className="ms-Grid"
                  ref={(node) => {
                    this.dropzoneRef = node;
                  }}
                  accept="image/jpeg, image/png"
                  onDrop={this.handleUpload.bind(this)}
                >
                  <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm12">
                      <Label required={true}>{t('Image')}</Label>
                    </div>
                  </div>

                  <div
                    className="ms-Grid-row"
                    style={{ marginBottom: '0.5em' }}
                  >
                    <div className="ms-Grid-col ms-lg12">
                      <PrimaryButton
                        disabled={this.dropzoneState.uploadInProgress}
                        onClick={() => this.dropzoneRef.open()}
                      >
                        {this.dropzoneState.uploadInProgress
                          ? t('Uploading') + '...'
                          : t('Upload')}
                      </PrimaryButton>
                    </div>
                  </div>

                  {this.uploadError && (
                    <MessageBar messageBarType={MessageBarType.error}>
                      {this.uploadError}
                    </MessageBar>
                  )}
                </Dropzone>
              )}
              {this.image && (
                <Fragment>
                  <div>
                    <Image
                      src={this.generatePreviewUrl(this.image)}
                      {...previewProperties}
                    />
                  </div>
                  <div>
                    <DefaultButton onClick={this.handleRemoveImage.bind(this)}>
                      {t('Remove')}
                    </DefaultButton>
                  </div>
                </Fragment>
              )}
            </div>
          </div>

          <SaleCreatePanelContent
            data={this.data}
            onChanged={this.handleChanged}
            onIsValidChanged={this.handleContentIsValidChanged}
            intl={intl}
            t={t}
          />

          {app.canMls && (
            <Fragment>
              <div className="ms-Grid-row">
                <div className="ms-Grid-col ms-sm12">
                  <Dropdown
                    label={t('MLS')}
                    selectedKey={this.data['mls']}
                    onChange={this.localHandleChange('mls')}
                    options={config.mlsTypes.map((key) => {
                      return { key: key, text: t(`mls::${key}`) };
                    })}
                    required={true}
                  />
                </div>
              </div>
              <div className="ms-Grid-row">
                <div className="ms-Grid-col ms-sm12">
                  <Checkbox
                    onChange={this.handleInviteOnly}
                    label={t('Invite only')}
                    disabled={!this.canInviteOnly}
                  />
                </div>
              </div>
            </Fragment>
          )}

          <div className="ms-Grid-row">
            <div className="ms-Grid-col ms-sm12 ms-lg12">
              <BehalfOfAgentsDropdown
                agents={this.sale.agents}
                selectedAgent={this.sale.selectedAgent}
                onAgentChanged={this.handleAgentChanged}
              />
            </div>
          </div>

          {this.sale.config.features.packageAuctions && (
            <div className="ms-Grid-row">
              <div className="ms-Grid-col ms-sm12">
                <Dropdown
                  label={t('Select master sale')}
                  selectedKey={this.sale.masterSaleId}
                  onChange={this.handleMasterSaleId}
                  options={[
                    { text: t('not part of master sale'), key: '' },
                    { text: t('is master sale'), key: 'master' },
                    ...this.sale.masterSales,
                  ]}
                />
              </div>
            </div>
          )}

          {this.sale.config.features.allowSelfRegistration &&
            (!this.sale.masterSaleId ||
              this.sale.masterSaleId === 'master') && (
              <div className="ms-Grid-row">
                <div className="ms-Grid-col ms-sm12">
                  <Checkbox
                    onChange={this.handleSelfRegistration}
                    value={this.sale.selfRegistration}
                    label={t('Self registration')}
                  />
                </div>
              </div>
            )}
        </div>
      </Panel>
    );
  }
}

export default SaleCreatePanel;



// WEBPACK FOOTER //
// ./src/app/components/SaleCreatePanel/index.jsx